import { useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { openLink } from '@tma.js/sdk-react';
import { Check, ExternalLink, Plus, Swords, X } from 'lucide-react';

import { useHaptics } from '@/hooks/useHaptics';
import { useMainButton } from '@/hooks/useMainButton';
import { Loader } from '@/components/common/Loader';
import { ChessStatusBadge } from '@/components/chess/ChessStatusBadge';
import { UserAvatar } from '@/components/social/UserAvatar';
import { getMyLogtoSub } from '@/lib/logto';
import {
  CHESS_CHALLENGE_FORMATS,
  CHESS_CLOCK_PRESETS,
  type Challenge,
  type ChessChallengeFormat,
} from '@/types/qchess';
import {
  useAcceptChessChallengeMutation,
  useCreateChessChallengeMutation,
  useDeclineChessChallengeMutation,
  useListChessChallengesQuery,
} from '@/services/qchessApi';
import { classifyQchessError, qchessErrorKey } from '@/services/qchessErrors';
import { useListFriendsQuery } from '@/services/socialApi';
import { cn } from '@/lib/cn';

/**
 * Chess duels — incoming/outgoing challenge lists with accept/decline, a
 * "new duel" create form (opponent QLASH id + format + clock for the MVP), and
 * an "Open game" deeplink + embedded board for in-progress challenges.
 */
export function ChessChallenges() {
  const { t } = useTranslation('chess');
  const haptics = useHaptics();

  const [formOpen, setFormOpen] = useState(false);
  const me = getMyLogtoSub();

  // Poll while the duels screen is open so the challenger sees the opponent
  // accept (pending → in_progress) and the game finish (→ completed) without a
  // manual refresh; refetchOnMountOrArgChange covers re-entry to the screen.
  const listQuery = useListChessChallengesQuery(undefined, {
    pollingInterval: 6000,
    refetchOnMountOrArgChange: true,
  });
  const challenges = listQuery.data?.challenges ?? [];

  // Split by side relative to the signed-in user. If we can't resolve `me`
  // (no token decoded), everything falls into "outgoing" so the lists still
  // render — accept/decline buttons gate on `incoming` so they won't appear.
  const { incoming, outgoing } = useMemo(() => {
    const inc: Challenge[] = [];
    const out: Challenge[] = [];
    for (const c of challenges) {
      if (me && c.opponentLogtoUserId === me) inc.push(c);
      else out.push(c);
    }
    return { incoming: inc, outgoing: out };
  }, [challenges, me]);

  return (
    <div className="flex flex-col gap-4 px-4 pb-6 pt-5">
      <header className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand/15 text-brand">
            <Swords aria-hidden className="h-5 w-5" />
          </span>
          <h1 className="text-2xl font-bold text-fg">{t('challenges.title')}</h1>
        </div>
        <button
          type="button"
          onClick={() => {
            haptics.impact('light');
            setFormOpen((v) => !v);
          }}
          className="flex items-center gap-1 rounded-lg bg-brand px-3 py-2 text-xs font-bold text-brand-fg transition-opacity active:opacity-80"
        >
          {formOpen ? (
            <X aria-hidden className="h-4 w-4" />
          ) : (
            <Plus aria-hidden className="h-4 w-4" />
          )}
          {t('challenges.create')}
        </button>
      </header>

      {formOpen && <CreateChallengeForm onCreated={() => setFormOpen(false)} />}

      {listQuery.isLoading ? (
        <Loader />
      ) : listQuery.error ? (
        <p className="rounded-md bg-danger/15 px-3 py-2 text-sm text-danger" role="alert">
          {t(qchessErrorKey(classifyQchessError(listQuery.error)))}
        </p>
      ) : challenges.length === 0 ? (
        <p className="py-8 text-center text-sm text-fg-muted">{t('challenges.empty')}</p>
      ) : (
        <>
          {incoming.length > 0 && (
            <section className="flex flex-col gap-2">
              <h2 className="text-xs font-semibold uppercase tracking-wide text-fg-muted">
                {t('challenges.incoming')}
              </h2>
              {incoming.map((c) => (
                <ChallengeRow key={c._id} challenge={c} incoming />
              ))}
            </section>
          )}
          {outgoing.length > 0 && (
            <section className="flex flex-col gap-2">
              <h2 className="text-xs font-semibold uppercase tracking-wide text-fg-muted">
                {t('challenges.outgoing')}
              </h2>
              {outgoing.map((c) => (
                <ChallengeRow key={c._id} challenge={c} incoming={false} />
              ))}
            </section>
          )}
        </>
      )}

      <p className="px-1 text-[11px] leading-snug text-fg-muted">{t('disclaimer')}</p>
    </div>
  );
}

/* ── Row ──────────────────────────────────────────────────────────────── */

function ChallengeRow({
  challenge,
  incoming,
}: {
  challenge: Challenge;
  incoming: boolean;
}) {
  const { t } = useTranslation('chess');
  const haptics = useHaptics();

  const [accept, { isLoading: accepting }] = useAcceptChessChallengeMutation();
  const [decline, { isLoading: declining }] = useDeclineChessChallengeMutation();

  const opponentName = incoming
    ? challenge.challengerUsername
    : challenge.opponentUsername;

  const onAccept = async () => {
    haptics.impact('medium');
    try {
      await accept(challenge._id).unwrap();
      haptics.notification('success');
    } catch {
      haptics.notification('error');
    }
  };

  const onDecline = async () => {
    haptics.selection();
    try {
      await decline(challenge._id).unwrap();
    } catch {
      haptics.notification('error');
    }
  };

  return (
    <div className="flex flex-col gap-3 rounded-xl bg-bg-secondary p-3">
      <Link
        to={`/chess/challenges/${challenge._id}`}
        onClick={() => haptics.impact('light')}
        className="flex items-center gap-2"
      >
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-fg">
            <span className="text-fg-muted">{t('challenges.vs')} </span>
            {opponentName}
          </p>
          <p className="mt-0.5 flex items-center gap-2 text-[11px] text-fg-muted">
            <span className="uppercase">{t(`format.${challenge.format}`)}</span>
            <span aria-hidden>·</span>
            <span>
              {t('challenges.score', {
                a: challenge.winsChallenger,
                b: challenge.winsOpponent,
              })}
            </span>
          </p>
        </div>
        <ChessStatusBadge status={challenge.status} variant="challenge" />
      </Link>

      {/* Accept / decline (only for incoming pending challenges) */}
      {incoming && challenge.status === 'pending' && (
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => void onAccept()}
            disabled={accepting || declining}
            className="flex-1 rounded-lg bg-brand px-3 py-2 text-xs font-bold uppercase tracking-wide text-brand-fg transition-opacity active:opacity-80 disabled:opacity-50"
          >
            {accepting ? t('challenges.accepting') : t('challenges.accept')}
          </button>
          <button
            type="button"
            onClick={() => void onDecline()}
            disabled={accepting || declining}
            className="flex-1 rounded-lg bg-bg-tertiary px-3 py-2 text-xs font-bold uppercase tracking-wide text-fg-muted transition-opacity active:opacity-80 disabled:opacity-50"
          >
            {declining ? t('challenges.declining') : t('challenges.decline')}
          </button>
        </div>
      )}

      {/* In-progress game: open + embedded board */}
      {challenge.status === 'in_progress' && challenge.currentGameId && (
        <>
          <button
            type="button"
            onClick={() => {
              haptics.impact('medium');
              openLink.ifAvailable(`https://lichess.org/${challenge.currentGameId}`);
            }}
            className="flex items-center justify-center gap-2 rounded-lg bg-brand px-3 py-2 text-xs font-bold uppercase tracking-wide text-brand-fg transition-opacity active:opacity-80"
          >
            <ExternalLink aria-hidden className="h-4 w-4" />
            {t('challenges.openGame')}
          </button>
          {/* TODO(board-client): replace this read-only embed with a native
              interactive board once a qchess-api Lichess Board API proxy
              exists. For the MVP the embed is view/replay only; moves are
              played via the deeplink above on lichess.org. */}
          <div className="overflow-hidden rounded-lg border border-border">
            <iframe
              title={t('challenges.boardTitle')}
              src={`https://lichess.org/embed/${encodeURIComponent(challenge.currentGameId)}?theme=dark&bg=dark`}
              className="h-[360px] w-full"
              loading="lazy"
              sandbox="allow-scripts allow-same-origin allow-popups"
            />
          </div>
        </>
      )}
    </div>
  );
}

/* ── Create form ──────────────────────────────────────────────────────── */

function CreateChallengeForm({ onCreated }: { onCreated: () => void }) {
  const { t } = useTranslation('chess');
  const haptics = useHaptics();

  const [opponentId, setOpponentId] = useState('');
  const [format, setFormat] = useState<ChessChallengeFormat>('bo1');
  const [clockId, setClockId] = useState(CHESS_CLOCK_PRESETS[1].id); // blitz
  const [rated, setRated] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Opponent is picked from the user's QSocials friends — `friendUserId` is the
  // Logto sub, exactly the key qchess-api looks up LinkedAccount by. (The old
  // free-text "paste QLASH id" field sent arbitrary strings that almost never
  // matched a Logto sub, producing false "opponent not linked" errors.)
  const friendsQuery = useListFriendsQuery();
  const friends = friendsQuery.data ?? [];

  const [createChallenge, { isLoading }] = useCreateChessChallengeMutation();

  const preset = useMemo(
    () => CHESS_CLOCK_PRESETS.find((p) => p.id === clockId) ?? CHESS_CLOCK_PRESETS[1],
    [clockId],
  );

  const valid = opponentId.length > 0;

  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!valid || isLoading) return;
    setError(null);
    try {
      await createChallenge({
        opponentLogtoUserId: opponentId,
        format,
        clockLimitSeconds: preset.clockLimitSeconds,
        clockIncrementSeconds: preset.clockIncrementSeconds,
        rated,
      }).unwrap();
      haptics.notification('success');
      onCreated();
    } catch (err) {
      haptics.notification('error');
      setError(t(qchessErrorKey(classifyQchessError(err))));
    }
  };

  useMainButton({
    text: t('challenges.form.submit'),
    enabled: valid && !isLoading,
    loading: isLoading,
    onClick: () => void submit(),
  });

  return (
    <form
      onSubmit={submit}
      className="flex flex-col gap-4 rounded-xl bg-bg-secondary p-4"
    >
      <h2 className="text-sm font-bold text-fg">{t('challenges.form.title')}</h2>

      <div className="flex flex-col gap-1">
        <span className="text-xs font-semibold uppercase tracking-wide text-fg-muted">
          {t('challenges.form.opponent')}
        </span>
        {friendsQuery.isLoading ? (
          <div className="flex h-16 items-center justify-center">
            <Loader />
          </div>
        ) : friendsQuery.error ? (
          <p className="rounded-md bg-danger/15 px-3 py-2 text-sm text-danger" role="alert">
            {t('challenges.form.friendsError')}
          </p>
        ) : friends.length === 0 ? (
          <div className="rounded-lg bg-bg-tertiary px-3 py-3 text-sm text-fg-muted">
            {t('challenges.form.noFriends')}{' '}
            <Link to="/friends" className="font-semibold text-brand">
              {t('challenges.form.goToFriends')}
            </Link>
          </div>
        ) : (
          <div className="flex max-h-56 flex-col gap-1.5 overflow-y-auto">
            {friends.map((f) => {
              const selected = opponentId === f.friendUserId;
              const name = f.displayName || t('challenges.form.unknownFriend');
              return (
                <button
                  key={f.friendUserId}
                  type="button"
                  onClick={() => {
                    haptics.selection();
                    setError(null);
                    setOpponentId(f.friendUserId);
                  }}
                  className={cn(
                    'flex items-center gap-3 rounded-lg px-3 py-2 text-left transition-opacity active:opacity-80',
                    selected ? 'bg-brand/15 ring-1 ring-brand' : 'bg-bg-tertiary',
                  )}
                >
                  <UserAvatar
                    userId={f.friendUserId}
                    label={f.displayName ?? undefined}
                    avatarUrl={f.avatarUrl}
                    size="sm"
                  />
                  <span className="min-w-0 flex-1 truncate text-sm font-semibold text-fg">
                    {name}
                  </span>
                  {selected && <Check aria-hidden className="h-4 w-4 shrink-0 text-brand" />}
                </button>
              );
            })}
          </div>
        )}
        <span className="text-[11px] text-fg-muted">{t('challenges.form.opponentHint')}</span>
      </div>

      {/* Format */}
      <fieldset className="flex flex-col gap-1">
        <legend className="mb-1 text-xs font-semibold uppercase tracking-wide text-fg-muted">
          {t('challenges.form.format')}
        </legend>
        <div className="flex gap-2">
          {CHESS_CHALLENGE_FORMATS.map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => {
                haptics.selection();
                setFormat(f);
              }}
              className={cn(
                'flex-1 rounded-lg px-2 py-2 text-[11px] font-bold uppercase tracking-wide transition-opacity active:opacity-80',
                format === f ? 'bg-brand text-brand-fg' : 'bg-bg-tertiary text-fg-muted',
              )}
            >
              {f}
            </button>
          ))}
        </div>
      </fieldset>

      {/* Clock */}
      <fieldset className="flex flex-col gap-1">
        <legend className="mb-1 text-xs font-semibold uppercase tracking-wide text-fg-muted">
          {t('challenges.form.clock')}
        </legend>
        <div className="flex gap-2">
          {CHESS_CLOCK_PRESETS.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => {
                haptics.selection();
                setClockId(p.id);
              }}
              className={cn(
                'flex-1 rounded-lg px-2 py-2 text-[11px] font-bold transition-opacity active:opacity-80',
                clockId === p.id ? 'bg-brand text-brand-fg' : 'bg-bg-tertiary text-fg-muted',
              )}
            >
              {t(`clock.${p.id}`)}
            </button>
          ))}
        </div>
      </fieldset>

      {/* Rated */}
      <label className="flex items-center justify-between gap-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-fg-muted">
          {t('challenges.form.rated')}
        </span>
        <input
          type="checkbox"
          checked={rated}
          onChange={(e) => {
            haptics.selection();
            setRated(e.target.checked);
          }}
          className="h-5 w-5 accent-[var(--color-brand)]"
        />
      </label>

      {error && (
        <p className="rounded-md bg-danger/15 px-3 py-2 text-sm text-danger" role="alert">
          {error}
        </p>
      )}
    </form>
  );
}
